/* eslint-disable max-len */
import { SelectProps } from '@amzn/awsui-components-react-v3';

/**
 * Entry used for request table in supplier dashboard.
 */
export interface DashboardTableItem {
  rfxId: string;
  rfxRevision: number;
  rfxName: string;
  rfxType: string;
  buyer: string;
  status: string;
  createdAt: number;
  dueAt: number;
  decisionTime: number | undefined;
}

export interface SupplierDashboardTableItem extends DashboardTableItem {}

export enum RFXActor {
  BUYER = 'BUYER',
  SUPPLIER = 'SUPPLIER'
}

export interface SupplierRFXEntry {
  rfxId: string;
  rfxType: string;
  rfxName: string;
  buyer: string;
  requestStatus: string;
  beginDate: string;
  dueDate: string;
}

export interface QuoteEntry {
  attachmentId: string;
  uploadStatus: RFXQuotesFileUploadStatus;
  processStatus: RFXQuotesFileProcessStatus;
  version: number;
  uploadDate: string;
  lastModifiedDate?: string;
  fileName: string;
  fileURL: string;
  fileSize: string;
  format: string;
  stagingS3Uri: string;
  report: string;
  reportURL: string;
  message: string;
  description: string;
}

export interface QuoteTemplateGenerationStatus {
  attempt: number;
  generationStatus: RFXQuoteTemplateGenerationProcessStatus;
  message?: string;
  fileName?: string;
  fileURL?: string;
}

export interface DocumentEntry {
  attachmentId: string;
  type: SelectProps.Option;
  version: number;
  format: string;
  description: string;
  attachmentName: string;
  attachmentURL: string;
  stagingS3Uri: string;
  createdDate: string;
  requestedBy: string;
  documentValidUntil: string;
}

export interface PhoneNumber {
  countryCode: string;
  number: number;
}

export interface Contact {
  name: string;
  title?: string;
  email?: string;
  primaryPhoneNumber: PhoneNumber;
  secondaryPhoneNumber?: PhoneNumber;
  contactType: string;
}

export enum ContactType {
  DISPATCH = 'DISPATCH',
  ESCALATION = 'ESCALATION',
  ACCOUNT = 'ACCOUNT'
}

export interface Contract {
  contractCentralId?: string;
  termType: string;
  expiryDate?: number;
  effectiveDate: number;
  renewalType?: string;
}

export enum ContractTermType {
  EXPIRES = 'EXPIRES',
  DOES_NOT_EXPIRE = 'DOES_NOT_EXPIRE'
}

export enum ContractRenewalType {
  TACIT_RENEWABLE = 'TACIT_RENEWABLE',
  NON_EXTENDABLE = 'NON_EXTENDABLE',
  AUTOMATIC_LIMITED = 'AUTOMATIC_LIMITED',
  AUTOMATIC_INDEFINITE = 'AUTOMATIC_INDEFINITE'
}

/**
 * The file upload status from create/update attachment API
 */
export enum RFXQuotesFileUploadStatus {
  CREATED = 'CREATED',
  UPLOADED = 'UPLOADED',
  UPDATED = 'UPDATED',
  ERROR = 'ERROR',
  TOMBSTONED = 'TOMBSTONED'
}

/**
 * The file process request status from bulk process API excluding line items status
 */
export enum RFXQuotesFileProcessStatus {
  NODATA = 'NO DATA',
  SUBMITTED = 'SUBMITTED',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED'
}

/**
 * The quote template generation process status from generation status API.
 */
export enum RFXQuoteTemplateGenerationProcessStatus {
  SUBMITTED = 'SUBMITTED',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED'
}

export type Nullable<T> = T | null;

export type NullableString = string | null;

export type NullableStringArray = string[] | null;

export type StringMap = { [key: string]: string };

export type KeyValueMap<T> = { [key: string]: T };

export type Func<T> = (args: T) => void;

/**
 * LDAP and other groups used for referencing access levels for the application.
 * See lambda package below for internal and external mappings.
 * See infrastructure package below for API/resource access for each group.
 *
 * https://code.amazon.com/packages/AWSSCMUIDevClientCognitoUserMappingLambda/blobs/mainline/--/configuration/user-group-mapping/external_user_group_mapping.json
 * https://code.amazon.com/packages/AWSSCMUIDevClientCognitoUserMappingLambda/blobs/mainline/--/configuration/user-group-mapping/user_group_mapping.json
 * https://code.amazon.com/packages/AWSSCMUIDevClientInfrastructure/blobs/mainline/--/configuration/jinja/template.jinja
 *
 * For HWEng and PAC check AWSSCMHWEngClientCognitoUserMappingLambda and AWSSCMHWEngClientInfrastructure packages with the same file names as above.
 * For DCGS check AWSSCMDCGSClientCognitoUserMappingLambda and AWSSCMDCGSClientInfrastructure packages with the same file names as above.
 */
export enum UserPermissionGroup {
  COMBINED_UI_DEV_BUYER_ADVANCED_GROUP = 'aws-scm-ui-dev-ip-full-prod-and-smart-advanced',
  COMBINED_UI_DEV_BUYER_BASIC_GROUP = 'aws-scm-ui-dev-ip-full-prod-and-smart-basic',
  COMBINED_UI_DEV_DEVELOPER_GROUP = 'aws-scm-ui-dev-ip-full-prod-and-smart-devs',
  COMBINED_UI_DEV_READONLY_GROUP = 'aws-scm-ui-dev-ip-full-prod-and-smart-readonly',
  COMBINED_DCGS_BUYER_ADVANCED_GROUP = 'aws-scm-dcgs-townsend-readonly-and-smart-advanced',
  COMBINED_DCGS_BUYER_BASIC_GROUP = 'aws-scm-dcgs-townsend-readonly-and-smart-basic',
  COMBINED_DCGS_READONLY_GROUP = 'aws-scm-dcgs-townsend-readonly-and-smart-readonly',
  COMBINED_HWENG_BUYER_ADVANCED_GROUP = 'scm-argo-hw-readonly-and-vm-and-ordering-full-and-smart-advanced-hweng',
  COMBINED_HWENG_BUYER_BASIC_GROUP = 'scm-argo-hw-readonly-and-vm-and-ordering-full-and-smart-basic-hweng',
  COMBINED_HWENG_READONLY_GROUP = 'scm-argo-hw-readonly-and-vm-and-ordering-full-and-smart-readonly-hweng',
  COMBINED_PAC_BUYER_ADVANCED_GROUP = 'scm-argo-hw-readonly-and-vm-and-ordering-full-and-smart-advanced-pac',
  COMBINED_PAC_BUYER_BASIC_GROUP = 'sscm-argo-hw-readonly-and-vm-and-ordering-full-and-smart-basic-pac',
  COMBINED_PAC_READONLY_GROUP = 'scm-argo-hw-readonly-and-vm-and-ordering-full-and-smart-readonly-pac',

  AUTH_V2_NON_PROD_ADVANCED_GROUP = 'aws-scm-smart-advanced-pre-prod-only',
  AUTH_V2_NON_PROD_BASIC_GROUP = 'aws-scm-smart-basic-pre-prod-only',
  AUTH_V2_NON_PROD_READONLY_GROUP = 'aws-scm-smart-readonly-pre-prod-only',

  AUTH_V2_PROD_ADVANCED_GROUP = 'aws-scm-smart-advanced-prod-only',
  AUTH_V2_PROD_BASIC_GROUP = 'aws-scm-smart-basic-prod-only',
  AUTH_V2_PROD_READONLY_GROUP = 'aws-scm-smart-readonly-prod-only',

  SMART_DEV_GROUP = 'aws-scm-offers'
}

export const ALL_PERMISSION_GROUPS_LIST: string[] = Object.values(UserPermissionGroup);

export const ALL_PERMISSION_GROUPS_SET: Set<string> = new Set(ALL_PERMISSION_GROUPS_LIST);

export const UI_DEV_PERMISSION_GROUPS_SET: Set<string> = new Set(
  ALL_PERMISSION_GROUPS_LIST.filter((group) => !group.includes('dcgs'))
    .filter((group) => !group.includes('hweng'))
    .filter((group) => !group.includes('pac'))
);

export const DCGS_PERMISSION_GROUPS_SET: Set<string> = new Set(
  ALL_PERMISSION_GROUPS_LIST.filter((group) => group.includes('dcgs'))
);

export const HWENG_PERMISSION_GROUPS_SET: Set<string> = new Set(
  ALL_PERMISSION_GROUPS_LIST.filter((group) => group.includes('hweng'))
);

export const PAC_PERMISSION_GROUPS_SET: Set<string> = new Set(
  ALL_PERMISSION_GROUPS_LIST.filter((group) => group.includes('pac'))
);

export const BUYER_ADVANCED_PERMISSION_GROUPS_SET: Set<string> = new Set(
  ALL_PERMISSION_GROUPS_LIST.filter((group) => group.includes('smart-advanced'))
);

export const BUYER_BASIC_PERMISSION_GROUPS_SET: Set<string> = new Set(
  ALL_PERMISSION_GROUPS_LIST.filter((group) => group.includes('smart-basic'))
);

export const DEVELOPER_PERMISSION_GROUPS_SET: Set<string> = new Set([
  ...ALL_PERMISSION_GROUPS_LIST.filter((group) => group.includes('smart-devs')),
  UserPermissionGroup.SMART_DEV_GROUP
]);

export const READONLY_PERMISSION_GROUPS_SET: Set<string> = new Set(
  ALL_PERMISSION_GROUPS_LIST.filter((group) => group.includes('smart-readonly'))
);

export const INTEGRATION_TEST_USER: string = 'svc-prtl-dev';

export interface CoManagerEntry {
  alias: string;
  firstName?: string;
  lastName?: string;
  title?: string;
  department?: string;
}

export enum RFQEntity {
  REQUEST = 'RFQREQUEST',
  RESPONSE = 'RFQRESPONSE',
  LINEITEM = 'RFQLINEITEM',
  QUESTION = 'RFQQUESTION'
}

export type SubCategoryMap = { [key: string]: string[] };
